import { motion } from "framer-motion";
import { Calculator, Euro, Droplet, Sofa, Percent, RotateCcw } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Parameters, formatEur } from "@/lib/index";

interface ParametersPanelProps {
  params: Parameters;
  onUpdate: (field: keyof Parameters, value: number | boolean) => void;
  p2Contribution: number;
  billingBenefit: number;
}

export function ParametersPanel({ params, onUpdate, p2Contribution, billingBenefit }: ParametersPanelProps) {
  const furnitureAdjustment = params.includeFurniture ? params.furnitureCost : 0;

  return (
    <motion.div
      initial={{ opacity: 0, x: -24 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ type: "spring", stiffness: 300, damping: 35 }}
    >
      <Card className="p-6 space-y-6">
        <div className="flex items-center gap-3 pb-4 border-b border-border">
          <div className="p-2 rounded-lg bg-primary/10">
            <Calculator className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">Parámetros de Análisis</h3>
            <p className="text-sm text-muted-foreground">Ajusta los valores para recalcular</p>
          </div>
        </div>

        <div className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="p1-contribution" className="flex items-center gap-2 text-sm font-medium">
              <Euro className="w-4 h-4 text-chart-1" />
              Aportación anual fija P1 (€)
            </Label>
            <div className="relative">
              <Input
                id="p1-contribution"
                type="number"
                value={params.p1FixedContribution}
                onChange={(e) => onUpdate('p1FixedContribution', parseFloat(e.target.value) || 0)}
                className="pr-8"
                step="100"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">€</span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="p2-eur-litre" className="flex items-center gap-2 text-sm font-medium">
              <Droplet className="w-4 h-4 text-chart-2" />
              P2 €/litro
            </Label>
            <div className="relative">
              <Input
                id="p2-eur-litre"
                type="number"
                value={params.p2EurPerLitre}
                onChange={(e) => onUpdate('p2EurPerLitre', parseFloat(e.target.value) || 0)}
                className="pr-8"
                step="0.01"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">€</span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="p2-litres" className="flex items-center gap-2 text-sm font-medium">
              <Droplet className="w-4 h-4 text-chart-2" />
              P2 Litros vendidos
            </Label>
            <div className="relative">
              <Input
                id="p2-litres"
                type="number"
                value={params.p2LitresSold}
                onChange={(e) => onUpdate('p2LitresSold', parseFloat(e.target.value) || 0)}
                className="pr-8"
                step="1000"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">L</span>
            </div>
          </div>

          <div className="space-y-3 pt-2">
            <Label htmlFor="furniture-cost" className="flex items-center gap-2 text-sm font-medium">
              <Sofa className="w-4 h-4 text-accent" />
              Coste mobiliario P2 (€)
            </Label>
            <div className="relative">
              <Input
                id="furniture-cost"
                type="number"
                value={params.furnitureCost}
                onChange={(e) => onUpdate('furnitureCost', parseFloat(e.target.value) || 0)}
                className="pr-8"
                step="100"
                disabled={!params.includeFurniture}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">€</span>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
              <Label htmlFor="include-furniture" className="text-sm cursor-pointer">
                Incluir coste de mobiliario
              </Label>
              <Switch
                id="include-furniture"
                checked={params.includeFurniture}
                onCheckedChange={(checked) => onUpdate('includeFurniture', checked)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="vat-rate" className="flex items-center gap-2 text-sm font-medium">
              <Percent className="w-4 h-4 text-warning" />
              IVA (%)
            </Label>
            <div className="relative">
              <Input
                id="vat-rate"
                type="number"
                value={params.vatRate}
                onChange={(e) => onUpdate('vatRate', parseFloat(e.target.value) || 0)}
                className="pr-8"
                step="1"
                min="0"
                max="100"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
            </div>
          </div>

          <div className="pt-2 border-t border-border space-y-4">
            <h4 className="text-sm font-semibold text-muted-foreground flex items-center gap-2">
              <RotateCcw className="w-4 h-4" />
              Rappel fin de temporada
            </h4>
            <div className="space-y-2">
              <Label htmlFor="p1-rappel" className="flex items-center gap-2 text-sm font-medium">
                <Euro className="w-4 h-4 text-success" />
                Rappel total P1 (€)
              </Label>
              <div className="relative">
                <Input
                  id="p1-rappel"
                  type="number"
                  value={params.p1RappelAmount}
                  onChange={(e) => onUpdate('p1RappelAmount', parseFloat(e.target.value) || 0)}
                  className="pr-8"
                  step="0.01"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">€</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="p2-rappel" className="flex items-center gap-2 text-sm font-medium">
                <Euro className="w-4 h-4 text-success" />
                Rappel total P2 (€)
              </Label>
              <div className="relative">
                <Input
                  id="p2-rappel"
                  type="number"
                  value={params.p2RappelAmount}
                  onChange={(e) => onUpdate('p2RappelAmount', parseFloat(e.target.value) || 0)}
                  className="pr-8"
                  step="0.01"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">€</span>
              </div>
            </div>
          </div>
        </div>

        <div className="pt-4 border-t border-border space-y-3">
          <h4 className="text-sm font-semibold text-muted-foreground">Resumen de Aportaciones y Rappel</h4>
          <div className="space-y-2">
            <div className="flex items-center justify-between p-3 rounded-lg bg-chart-1/10">
              <span className="text-sm font-medium">P1 Aportación</span>
              <Badge variant="secondary" className="bg-chart-1/20 text-chart-1 hover:bg-chart-1/30">
                {formatEur(params.p1FixedContribution)}
              </Badge>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-chart-2/10">
              <div className="flex flex-col">
                <span className="text-sm font-medium">P2 Aportación</span>
                <span className="text-xs text-muted-foreground">
                  {params.p2EurPerLitre.toFixed(2)}€/L × {params.p2LitresSold.toLocaleString('es-ES')}L
                </span>
              </div>
              <Badge variant="secondary" className="bg-chart-2/20 text-chart-2 hover:bg-chart-2/30">
                {formatEur(p2Contribution)}
              </Badge>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-accent/10">
              <span className="text-sm font-medium">Mobiliario</span>
              <Badge
                variant="secondary"
                className={`${
                  params.includeFurniture
                    ? 'bg-accent/20 text-accent hover:bg-accent/30'
                    : 'bg-muted text-muted-foreground'
                }`}
              >
                {formatEur(furnitureAdjustment)}
              </Badge>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-success/10">
              <span className="text-sm font-medium">Rappel P1</span>
              <Badge variant="secondary" className="bg-success/20 text-success hover:bg-success/30">
                +{formatEur(params.p1RappelAmount)}
              </Badge>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-success/10">
              <span className="text-sm font-medium">Rappel P2</span>
              <Badge variant="secondary" className="bg-success/20 text-success hover:bg-success/30">
                +{formatEur(params.p2RappelAmount)}
              </Badge>
            </div>
            <div className="flex items-center justify-between p-3 rounded-lg bg-primary/10">
              <div className="flex flex-col">
                <span className="text-sm font-medium">Beneficio Facturación</span>
                <span className="text-xs text-muted-foreground">Calculado: P2 factura más que P1</span>
              </div>
              <Badge variant="secondary" className="bg-primary/20 text-primary hover:bg-primary/30">
                {formatEur(billingBenefit)}
              </Badge>
            </div>
          </div>
        </div>
      </Card>
    </motion.div>
  );
}